import { Controller } from "@hotwired/stimulus"

// Lets users copy one of the selected customer's stored addresses into the
// construction-site address field. The field stays fully editable afterwards.
//
// The book-icon button is disabled until a customer is selected. On open it
// fetches the customer's addresses (cached per customer) and renders them as a
// dropdown; picking one writes the formatted address into the field and fires
// input/change events so the map controller keeps working.
export default class extends Controller {
  static targets = ["customer", "field", "button", "menu", "list", "empty"]
  static values = { urlTemplate: String }

  connect() {
    this.addresses = null
    this.loadedCustomerId = null
    this.lastCustomerId = this.customerId
    this.boundClickOutside = this.clickOutside.bind(this)
    this.boundKeydown = this.handleKeydown.bind(this)
    this.updateButtonState()
  }

  disconnect() {
    this.stopListening()
  }

  // The customer SlimSelect re-emits spurious `change` events while the map SDK
  // mutates the DOM during init, which would otherwise close the menu in a loop.
  // Only react when the selected customer actually changed.
  customerChanged() {
    if (this.customerId === this.lastCustomerId) return

    this.lastCustomerId = this.customerId
    this.close()
    this.addresses = null
    this.loadedCustomerId = null
    this.updateButtonState()
  }

  get customerId() {
    return this.hasCustomerTarget ? this.customerTarget.value : ""
  }

  get open() {
    return this.hasMenuTarget && !this.menuTarget.classList.contains("hidden")
  }

  updateButtonState() {
    if (this.hasButtonTarget) {
      this.buttonTarget.disabled = this.customerId === ""
    }
  }

  // Open/close synchronously so the menu's state is settled within the click
  // itself; the async address fetch then fills the already-open menu.
  toggle(event) {
    event.preventDefault()
    if (this.customerId === "") return

    if (this.open) {
      this.close()
    } else {
      this.show()
    }
  }

  show() {
    this.menuTarget.classList.remove("hidden")
    this.startListening()
    this.loadAddresses()
  }

  close() {
    if (this.hasMenuTarget) {
      this.menuTarget.classList.add("hidden")
    }
    this.stopListening()
  }

  // Attach the outside-click listener on the next tick so the very click that
  // opened the menu can't immediately bubble up and close it again.
  startListening() {
    setTimeout(() => {
      document.addEventListener("click", this.boundClickOutside)
      document.addEventListener("keydown", this.boundKeydown)
    }, 0)
  }

  stopListening() {
    document.removeEventListener("click", this.boundClickOutside)
    document.removeEventListener("keydown", this.boundKeydown)
  }

  handleKeydown(event) {
    if (event.key === "Escape") this.close()
  }

  clickOutside(event) {
    if (this.buttonTarget.contains(event.target)) return
    if (this.menuTarget.contains(event.target)) return
    this.close()
  }

  async loadAddresses() {
    if (this.addresses !== null && this.loadedCustomerId === this.customerId) {
      this.render(this.addresses)
      return
    }

    const url = this.urlTemplateValue.replace(
      "__CUSTOMER_ID__",
      encodeURIComponent(this.customerId),
    )
    try {
      const response = await fetch(url, { headers: { Accept: "application/json" } })
      if (!response.ok) throw new Error("Request failed")
      const data = await response.json()
      this.addresses = data.addresses || []
    } catch {
      this.addresses = []
    }
    this.loadedCustomerId = this.customerId
    this.render(this.addresses)
  }

  render(addresses) {
    this.listTarget.replaceChildren()

    if (addresses.length === 0) {
      this.emptyTarget.classList.remove("hidden")
      return
    }

    this.emptyTarget.classList.add("hidden")
    addresses.forEach((address) => {
      const item = document.createElement("button")
      item.type = "button"
      item.className =
        "w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 focus:bg-gray-100 focus:outline-hidden"

      const label = document.createElement("span")
      label.className = "font-medium text-gray-900"
      label.textContent = `${address.category_label}: `
      item.appendChild(label)
      item.appendChild(document.createTextNode(address.full_address))

      item.addEventListener("click", () => this.select(address))
      this.listTarget.appendChild(item)
    })
  }

  select({ full_address: address, country }) {
    this.fieldTarget.value = address
    this.fieldTarget.dispatchEvent(new Event("input", { bubbles: true }))
    this.fieldTarget.dispatchEvent(new Event("change", { bubbles: true }))
    // Let the maps controller recenter the map/marker on the picked address. The
    // country travels along: the map geocodes inside the site's Land, and a customer
    // across the border would otherwise get no pin at all.
    this.dispatch("addressSelected", { detail: { address, country } })
    this.close()
    this.fieldTarget.focus()
  }
}
